import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add authentication token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add request/response interceptors for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error
      console.error('API Error:', error.response.data);
      
      // Handle authentication errors
      if (error.response.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    } else if (error.request) {
      // Request made but no response
      console.error('Network Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Hybrid API endpoints (combines Speedline and PAR)
export const hybridApi = {
  getDashboardMetrics: (date?: string, storeId?: string) =>
    api.get('/hybrid/dashboard-metrics', { params: { date, storeId } }),
  
  getStores: () => api.get('/hybrid/stores'),
  
  getStoresWithSales: (date?: string, limit?: number) =>
    api.get('/hybrid/stores-with-sales', { params: { date, limit } }),
  
  getStoresComparison: (storeIds: string[], date?: string) =>
    api.post('/hybrid/stores-comparison', { storeIds, date }),
  
  getDailySales: (date?: string, storeId?: string) =>
    api.get('/hybrid/sales/daily', { params: { date, storeId } }),
  
  getSalesRange: (
    startDate: string,
    endDate: string,
    storeId?: string,
    filters?: {
      revenueCenters?: string[];
      mealPeriods?: string[];
      paymentMethods?: string[];
      orderTypes?: string[];
      metrics?: string[];
    }
  ) => {
    const params: any = { startDate, endDate, storeId };

    // Add filter parameters if provided and not default "all"
    if (filters) {
      if (filters.revenueCenters && !filters.revenueCenters.includes('all')) {
        params.revenueCenters = filters.revenueCenters.join(',');
      }
      if (filters.mealPeriods && !filters.mealPeriods.includes('allDay')) {
        params.mealPeriods = filters.mealPeriods.join(',');
      }
      if (filters.paymentMethods && !filters.paymentMethods.includes('all')) {
        params.paymentMethods = filters.paymentMethods.join(',');
      }
      if (filters.orderTypes && !filters.orderTypes.includes('all')) {
        params.orderTypes = filters.orderTypes.join(',');
      }
      if (filters.metrics && filters.metrics.length > 0) {
        params.metrics = filters.metrics.join(',');
      }
    }

    return api.get('/hybrid/sales/range', { params });
  },
  
  getProductMix: (date?: string, storeId?: string) =>
    api.get('/hybrid/product-mix', { params: { date, storeId } }),
  
  getPaymentMethods: (date?: string, storeId?: string) =>
    api.get('/hybrid/payment-methods', { params: { date, storeId } }),
  
  getEmployeePerformance: (date?: string, storeId?: string) =>
    api.get('/hybrid/employee-performance', { params: { date, storeId } }),
  
  getHourlySales: (date?: string, storeId?: string) =>
    api.get('/hybrid/sales/hourly', { params: { date, storeId } }),
  
  getLaborCosts: (startDate: string, endDate: string, storeId?: string) =>
    api.get('/hybrid/labor-costs', { params: { startDate, endDate, storeId } }),
  
  getInventory: (storeId?: string) =>
    api.get('/hybrid/inventory', { params: { storeId } }),
};

// Sales API endpoints (using Hybrid service for Speedline/PAR)
export const salesApi = {
  getDailySales: (date?: string, storeId?: string) => 
    api.get('/hybrid/sales/daily', { params: { date, storeId } }),
  
  getDateRangeSales: (startDate: string, endDate: string, storeId?: string) =>
    api.get('/hybrid/sales/date-range', { params: { startDate, endDate, storeId } }),
  
  getMonthToDateSales: (date?: string, storeId?: string) =>
    api.get('/hybrid/sales/mtd', { params: { date, storeId } }),
  
  getWeekToDateSales: (date?: string, storeId?: string) =>
    api.get('/hybrid/sales/wtd', { params: { date, storeId } }),
  
  getYearToDateSales: (date?: string, storeId?: string) =>
    api.get('/hybrid/sales/ytd', { params: { date, storeId } }),
  
  getHourlySales: (date?: string, storeId?: string) =>
    api.get('/hybrid/sales/hourly', { params: { date, storeId } }),
  
  getTopItems: (date?: string, storeId?: string, limit?: number) =>
    api.get('/hybrid/top-items', { params: { date, storeId, limit } }),
  
  getCategoryBreakdown: (date?: string, storeId?: string) =>
    api.get('/hybrid/categories', { params: { date, storeId } }),
  
  getEmployeePerformance: (date?: string, storeId?: string) =>
    api.get('/hybrid/employee-performance', { params: { date, storeId } }),
  
  getPaymentAnalysis: (date?: string, storeId?: string) =>
    api.get('/hybrid/payment-methods', { params: { date, storeId } }),
  
  // Legacy endpoints (kept for compatibility)
  getTrailing12Months: (date?: string, storeId?: string) =>
    api.get('/sales/trailing-12-months', { params: { date, storeId } }),
  
  getProductMix: (date?: string, storeId?: string) =>
    api.get('/sales/product-mix', { params: { date, storeId } }),
  
  getLocations: () =>
    api.get('/sales/locations'),
  
  getModifierInsights: () =>
    api.get('/sales/modifier-insights'),
};

// Stores API endpoints
export const storesApi = {
  getAllStores: () => api.get('/hybrid/stores'),
  getStore: (id: string) => api.get(`/hybrid/stores/${id}`),
  getStoresByRegion: (region: string) => api.get(`/hybrid/stores/region/${region}`),
  getStoresWithSales: (date?: string, limit?: number) => 
    api.get('/hybrid/stores-with-sales', { params: { date, limit } }),
};

// Reports API endpoints
export const reportsApi = {
  sendDailyReport: (email: string, storeId?: string) =>
    api.post('/reports/daily', { email, storeId }),
  
  getReportPreview: (type: string, date?: string, storeId?: string) =>
    api.get(`/reports/preview/${type}`, { params: { date, storeId } }),
  
  sendTestEmail: (email: string) =>
    api.post('/reports/test-email', { email }),
};

export default api;